/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Trigger syncs with Fivetran", description = "The Fivetran extension lets you schedule Hightouch syncs to run as soon as a Fivetran job completes. Relying on Fivetran dbt transformations and connectors for sync scheduling ensures that Hightouch performs syncs when fresh data is available.", lastUpdatedDate = new Date(1682553600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Trigger syncs with Fivetran",
        description: "The Fivetran extension lets you schedule Hightouch syncs to run as soon as a Fivetran job completes. Relying on Fivetran dbt transformations and connectors for sync scheduling ensures that Hightouch performs syncs when fresh data is available.",
        lastUpdatedDate: "Thu Apr 27 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Connect to Fivetran",
          "depth": 0,
          "id": "connect-to-fivetran"
        }, {
          "value": "Configure your sync schedule",
          "depth": 0,
          "id": "configure-your-sync-schedule"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Forbidden access denied error",
          "depth": 1,
          "id": "forbidden-access-denied-error"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      ul: "ul",
      li: "li",
      h2: "h2",
      a: "a",
      strong: "strong",
      ol: "ol",
      img: "img",
      h3: "h3",
      code: "code"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "The Fivetran extension"
      }), "\n", _jsx(_components.p, {
        children: "The Fivetran extension lets you schedule Hightouch syncs to run as soon as a Fivetran job completes. Relying on Fivetran dbt transformations and connectors for sync scheduling ensures that Hightouch performs syncs when fresh data is available."
      }), "\n", _jsx(_components.p, {
        children: "Extension setup requires two steps:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Connecting your Fivetran account to Hightouch"
        }), "\n", _jsx(_components.li, {
          children: "Configuring your Hightouch syncs to trigger with Fivetran"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-fivetran",
        children: "Connect to Fivetran"
      }), "\n", _jsxs(_components.p, {
        children: ["Navigate to your Fivetran dashboard and ", _jsx(_components.a, {
          href: "https://fivetran.com/docs/rest-api/getting-started#gettingstarted",
          children: "retrieve your API Key and Secret"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The API Key and Secret must belong to a user with the ", _jsx(_components.a, {
            href: "https://fivetran.com/docs/getting-started/fivetran-dashboard/account-management/role-based-access-control#rolesinourrbacmodel",
            children: "Account Administrator role"
          }), " or you'll receive ", _jsx(_components.a, {
            href: "#forbidden-access-denied-error",
            children: "an error"
          }), " when trying to save your configuration."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["In Hightouch, enter your credentials in the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/extensions/fivetran/configuration",
          children: "Fivetran configuration page"
        }), " and click ", _jsx(_components.strong, {
          children: "Connect"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Behind the scenes, Hightouch adds a new ", _jsx(_components.a, {
          href: "https://fivetran.com/docs/rest-api/webhooks#signing",
          children: "signed"
        }), " webhook to your Fivetran account."]
      }), "\n", _jsx(_components.h2, {
        id: "configure-your-sync-schedule",
        children: "Configure your sync schedule"
      }), "\n", _jsxs(_components.p, {
        children: ["Now, when creating or modifying a sync you can select ", _jsx(_components.strong, {
          children: "Fivetran"
        }), " as a schedule type."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go the ", _jsx(_components.strong, {
            children: "Schedule"
          }), " tab of overview page of the sync you want to schedule. Select ", _jsx(_components.strong, {
            children: "Fivetran"
          }), " as the schedule type."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the appropriate ", _jsx(_components.strong, {
            children: "Fivetran workspace"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select one or more Fivetran connectors or Fivetran-managed dbt Core transformations as the ", _jsx(_components.strong, {
            children: "Fivetran jobs"
          }), " to trigger the sync."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/fivetran-configure.png",
          alt: "Fivetran schedule configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Your sync should is now scheduled to run whenever you chosen Fivetran jobs complete."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "forbidden-access-denied-error",
        children: "Forbidden access denied error"
      }), "\n", _jsxs(_components.p, {
        children: ["Ensure your Fivetran account has the ", _jsx(_components.a, {
          href: "https://fivetran.com/docs/getting-started/fivetran-dashboard/account-management/role-based-access-control#rolesinourrbacmodel",
          children: "Account Administrator role"
        }), ".\nOtherwise, you might encounter this error when saving your Fivetran configuration in Hightouch:\n", _jsx(_components.code, {
          children: "Forbidden access denied. Required permission: ACCOUNT_SECURITY_MANAGE"
        })]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
